export interface PresetConfig {
  tier: string;
  engine: string;
  modulus?: number;
  atbashBonus: boolean;
  enableDiagonalChecks: boolean;
  useLeap: boolean;
  symmetryMode: string;
  symmetryOrder: number;
  enforceDiagonalMirror: boolean;
  initializerType: string;
  density: number;
  seedValue: number;
  colorMode: string;
}

export interface PresetData {
  name: string;
  category: string;
  config: PresetConfig;
  description?: string;
}

export interface StreamRequest {
  configuration: PresetConfig;
  height: number;
  width: number;
  maxGenerations: number;
  streamIntervalMs: number;
}

export interface GenerationData {
  generation: number;
  grid: number[][];
}

export class StreamingService {
  private serverUrl: string;
  private abortController: AbortController | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  constructor() {
    this.serverUrl = this.resolveBaseUrl();
  }

  private resolveBaseUrl(): string {
    const customUrl = process.env.REACT_APP_FUNCTION_URL;
    if (customUrl) {
      return customUrl.replace(/\/$/, '');
    }

    const isDev = process.env.NODE_ENV === 'development';
    const origin = isDev ? 'http://localhost:9000' : window.location.origin;
    return `${origin.replace(/\/$/, '')}/api`;
  }

  async fetchPresets(): Promise<PresetData[]> {
    try {
      const response = await fetch(`${this.serverUrl}/presets/list`);
      if (!response.ok) {
        throw new Error('Failed to fetch presets');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching presets:', error);
      return [];
    }
  }

  async startStream(
    request: StreamRequest,
    onGeneration: (data: GenerationData) => void,
    onError: (error: string) => void,
    onComplete: () => void
  ): Promise<string> {
    this.abortController = new AbortController();
    let connectionId = '';
    
    try {
      console.log('Starting stream with config:', request.configuration);
      const response = await fetch(`${this.serverUrl}/stream/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to start stream');
      }

      this.reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!this.reader) {
        throw new Error('No response body');
      }

      // Start reading stream in background without awaiting
      const reader = this.reader;
      let buffer = '';
      let currentEventType = '';
      let connectionIdReceived = false;

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('Stream reading completed');
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const messages = buffer.split('\n\n');
            buffer = messages.pop() || '';

            for (const message of messages) {
              const lines = message.split('\n');
              let eventData = '';
              
              for (const line of lines) {
                if (line.startsWith('event:')) {
                  currentEventType = line.substring(6).trim();
                } else if (line.startsWith('data:')) {
                  eventData = line.substring(5).trim();
                }
              }

              if (eventData) {
                try {
                  const data = JSON.parse(eventData);
                  
                  // Handle events based on event type FIRST, not data properties
                  if (currentEventType === 'StreamStarted') {
                    connectionId = data.connectionId || connectionId;
                    connectionIdReceived = true;
                    console.log('🟢 Stream started:', connectionId);
                  } else if (currentEventType === 'GenerationData') {
                    if (data.grid && Array.isArray(data.grid)) {
                      console.log(`📊 Generation ${data.generation}: Grid ${data.grid.length}x${data.grid[0]?.length}, alive cells:`, data.grid.flat().filter(v => v > 0).length);
                      onGeneration({
                        generation: data.generation,
                        grid: data.grid
                      });
                    } else {
                      console.warn('⚠️ Invalid grid data for generation', data.generation, ':', data);
                    }
                  } else if (currentEventType === 'StreamCompleted') {
                    console.log('✅ Stream completed');
                  } else if (currentEventType === 'StreamError') {
                    console.error('❌ Stream error:', data.error);
                    onError(data.error || 'Stream error');
                  } else if (currentEventType === 'StatusUpdate') {
                    // Silent status update, don't log
                  } else {
                    console.warn('⚠️ Unknown event type:', currentEventType, 'data:', data);
                  }
                } catch (e) {
                  console.warn('Parse error for SSE data:', e, 'raw:', eventData);
                }
              }
            }
          }

          onComplete();
        } catch (error) {
          if ((error as any).name === 'AbortError') {
            console.log('🛑 Stream aborted by user');
          } else {
            console.error('❌ Stream reading error:', error);
            onError(error instanceof Error ? error.message : 'Unknown error');
          }
        } finally {
          this.reader = null;
          this.abortController = null;
        }
      };

      // Start reading stream in background
      readStream().catch(err => {
        console.error('Uncaught stream error:', err);
        onError(err instanceof Error ? err.message : 'Unknown error');
      });

      // Wait for connectionId to be received before returning
      let attempts = 0;
      while (!connectionIdReceived && attempts < 100) {
        await new Promise(resolve => setTimeout(resolve, 10));
        attempts++;
      }

      if (!connectionId) {
        throw new Error('Failed to receive connectionId from server');
      }

      console.log('✅ Returning connectionId:', connectionId);
      return connectionId;
    } catch (error) {
      console.error('❌ Stream error:', error);
      onError(error instanceof Error ? error.message : 'Unknown error');
      return '';
    }
  }

  async stopStream(connectionId: string): Promise<void> {
    console.log('🛑 stopStream called with connectionId:', connectionId);
    console.log('🛑 serverUrl:', this.serverUrl);
    console.log('🛑 Full URL:', `${this.serverUrl}/api/stream/stop/${connectionId}`);
    
    if (this.abortController) {
      console.log('🛑 Aborting fetch request');
      this.abortController.abort();
    }
    
    if (this.reader) {
      try {
        console.log('🛑 Cancelling reader');
        await this.reader.cancel();
      } catch (e) {
        console.warn('Reader cancel error:', e);
      }
    }
    
    try {
      const stopUrl = `${this.serverUrl}/stream/stop/${connectionId}`;
      console.log('🛑 Sending POST to:', stopUrl);
      const response = await fetch(stopUrl, {
        method: 'POST',
      });
      console.log('🛑 Stop response status:', response.status);
      const data = await response.json();
      console.log('🛑 Stop response data:', data);
      console.log('✅ Stream stop request sent and confirmed');
    } catch (error) {
      console.error('❌ Error stopping stream:', error);
    }
  }
}
